import { Component } from '@theme/component';
import { fetchConfig } from '@theme/utilities';
import { CartAddEvent, ThemeEvents } from '@theme/events';

/**
 * MARK:- Custom: Persistent floating Add to Cart bar for the product page.
 *
 * Adds the currently selected variant as a one-time purchase — it deliberately
 * ignores the main form's purchase-option (subscription) selection to stay a
 * frictionless quick-add. Variant selection IS respected (read live from the
 * native product form at click time) and quantity is taken from the bar's own
 * quantity picker. On a successful add the shopper is taken straight to the cart
 * page. The bar is shown once the native Add to Cart button scrolls out of view
 * and hidden while it is on screen.
 *
 * @typedef {object} FloatingBuyButtonsRefs
 * @property {HTMLButtonElement} button - The floating add to cart button.
 * @property {HTMLElement} [liveRegion] - The accessibility live region.
 * @extends Component<FloatingBuyButtonsRefs>
 */
export class FloatingBuyButtonsComponent extends Component {
  requiredRefs = ['button'];

  #abortController = new AbortController();

  /** @type {IntersectionObserver | undefined} */
  #observer;

  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#abortController;

    this.#setupObserver();

    // Keep the button's availability in sync with the variant chosen in the main form.
    const section = this.closest('.shopify-section') ?? document;
    section.addEventListener(ThemeEvents.variantUpdate, this.#onVariantUpdate, { signal });
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#abortController.abort();
    this.#observer?.disconnect();
  }

  /**
   * Shows the floating bar whenever the native Add to Cart button is out of view.
   */
  #setupObserver() {
    const target = this.#nativeProductForm;
    if (!target) return;

    this.#observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          // Show only once the native button has scrolled ABOVE the viewport (we've
          // passed it), not while it is still below the fold further down the page.
          const scrolledPast = !entry.isIntersecting && entry.boundingClientRect.top < 0;
          this.classList.toggle('is-visible', scrolledPast);
        }
      },
      { rootMargin: '0px 0px -32px 0px', threshold: 0 }
    );

    this.#observer.observe(target);
  }

  /**
   * The native product form on the page (the one this bar mirrors), scoped to the
   * product section so quick-add forms elsewhere are never picked up.
   * @returns {HTMLElement | null}
   */
  get #nativeProductForm() {
    const scope = this.closest('.shopify-section') ?? document;
    return scope.querySelector('product-form-component');
  }

  /**
   * The currently selected variant id, read live from the native form so variant
   * switches made before clicking the floating bar are honoured.
   * @returns {string}
   */
  get #variantId() {
    const form = this.#nativeProductForm;
    const input = /** @type {HTMLInputElement | null} */ (form?.querySelector('input[ref="variantId"], input[name="id"]'));
    return input?.value ?? this.dataset.variantId ?? '';
  }

  /**
   * The quantity chosen in the bar's own quantity picker. Falls back to 1 if the
   * picker is missing or holds a non-positive value.
   * @returns {number}
   */
  get #quantity() {
    const input = /** @type {HTMLInputElement | null} */ (this.querySelector('input[name="quantity"]'));
    const value = parseInt(input?.value ?? '', 10);
    return Number.isFinite(value) && value > 0 ? value : 1;
  }

  /**
   * Adds the current variant to the cart as a one-time purchase at the chosen quantity.
   * @param {MouseEvent} event
   */
  handleClick(event) {
    event.preventDefault();

    const { button } = this.refs;
    const variantId = this.#variantId;
    const quantity = this.#quantity;

    if (!variantId || button.disabled) return;

    const formData = new FormData();
    formData.append('id', variantId);
    formData.append('quantity', String(quantity));
    // One-time purchase: intentionally no `selling_plan`.

    button.disabled = true;
    button.classList.add('loading');

    const config = fetchConfig('javascript', { body: formData });

    fetch(Theme.routes.cart_add_url, {
      ...config,
      headers: {
        ...config.headers,
        Accept: 'text/html',
      },
    })
      .then((response) => response.json())
      .then((response) => {
        // On a cart error, surface it through the product form's channel
        // (`source: 'product-form-component'`) so the existing error toast handles it.
        if (response.status) {
          this.#announce(response.message);
          this.dispatchEvent(
            new CartAddEvent({}, this.id, {
              didError: true,
              source: 'product-form-component',
              productId: this.dataset.productId,
            })
          );
          return;
        }

        this.#announce(Theme.translations.added);
        // Take the shopper straight to the cart page after a successful add.
        window.location.assign(Theme.routes.cart_url);
      })
      .catch((error) => console.error(error))
      .finally(() => {
        // On success the page navigates away, so re-enabling here is a no-op;
        // on error it restores the button so the shopper can retry.
        button.disabled = false;
        button.classList.remove('loading');
      });
  }

  /**
   * @param {string} text
   */
  #announce(text) {
    const { liveRegion } = this.refs;
    if (liveRegion) liveRegion.textContent = text;
  }

  /**
   * @param {import('@theme/events').VariantUpdateEvent} event
   */
  #onVariantUpdate = (event) => {
    const { button } = this.refs;
    if (!button) return;

    const resource = event.detail?.resource;
    const soldOut = resource == null || resource.available === false;

    button.disabled = soldOut;
    button.classList.toggle('is-sold-out', soldOut);
  };
}

if (!customElements.get('floating-buy-buttons-component')) {
  customElements.define('floating-buy-buttons-component', FloatingBuyButtonsComponent);
}
